import { BaseResponse, QuerySchema } from "../base";
import { SocialChannel, YoutubePlaylist, MonetizationOptions } from "./model";
import { UsersListParamsBody, UserGetParamsBody, UserModel } from "./model";
import { sendReqToController } from "../network";
import { isSocketCall } from "@/utils";

interface RespUser extends BaseResponse {
  data: UserModel;
}

interface RespUserList extends BaseResponse {
  data: Array<UserModel>;
  count: number;
}

interface RespUserDelete extends BaseResponse {
  message: string;
}

const extractResult = (response: any): any => {
  return (
    response.result?.data?.result ?? response.result?.data ?? response.result
  );
};

export const GetMeApi = async (): Promise<RespUser> => {
  const response = await sendReqToController("user.me", {}, isSocketCall);
  try {
    const result = extractResult(response);
    const user: UserModel = result;

    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: "No User Found",
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.me response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Validate User Model",
      status: response.code,
    };
  }
};

export const GetUserApi = async (
  params: UserGetParamsBody
): Promise<RespUser> => {
  const response = await sendReqToController("user.get", params, isSocketCall);
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: "No User Found",
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.get response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Validate User Model",
      status: response.code,
    };
  }
};

export const ListUserApi = async (
  params: UsersListParamsBody
): Promise<RespUserList> => {
  const {
    name,
    team,
    rows: _rows,
    page: _page,
    foreign_keys: _foreign_keys,
    setInRedux: _setInRedux,
    ...restMust
  } = params || {};

  const query: QuerySchema = {
    size: params?.rows || 50,
    from: (!params?.page ? 0 : params?.page - 1) * (params?.rows || 50),
    foreign_keys: params?.foreign_keys ?? ["session"],
  };

  const mustFields = {
    ...restMust,
    ...(team?.length ? { team } : {}),
    status: [1],
  };

  query.must = mustFields;

  if (name) {
    const trimmed = name.trim().toLocaleLowerCase();
    if (trimmed.includes("@")) {
      query.should = {
        email: { qtype: "wildcard", value: `${trimmed}*` },
      };
    } else {
      const words = trimmed.split(/\s+/).filter(Boolean);
      query.must = { ...query.must, "name.sat": words };
    }
    query.min_should = 1;
  }

  query.sort = { mt: "desc" };

  const response = await sendReqToController("user.list", query, isSocketCall);
  try {
    const result = extractResult(response);
    const list: Array<UserModel> = [];
    if (Array.isArray(result?.list)) {
      list.push(...result.list);
    } else if (response.error !== "") {
      response.error = "invalid user list";
    }
    return {
      count: result?.count || 0,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing user.list response:", error, response);
    return {
      count: 0,
      data: [],
      error: "failed to validate user list response",
      status: response.code,
    };
  }
};

export const MostFrequentUserApi = async (
  params: UsersListParamsBody
): Promise<RespUserList> => {
  const query = {
    index: "user",
    key: params.key,
    mfu: params.mfu,
    terms: {} as Record<string, any>,
  } as QuerySchema;

  delete params.rows;
  delete params.page;
  delete params.foreign_keys;
  // delete params.key;
  query.terms = {
    ...params,
  };

  const response = await sendReqToController("mfu.list", query, isSocketCall);
  try {
    const result = extractResult(response);
    const list: Array<UserModel> = [];
    if (Array.isArray(result)) {
      list.push(...result);
    } else if (response.error !== "") {
      response.error = "invalid user list";
    }
    return {
      count: result?.count || list.length,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing mfu.list response:", error, response);
    return {
      count: 0,
      data: [],
      error: "failed to validate user list response",
      status: response.code,
    };
  }
};

export const MostFrequentUserApiNoIndex = async (
  params: UsersListParamsBody
): Promise<RespUserList> => {
  const query = {
    // index: 'user',
    key: params.key,
    mfu: params.mfu,
    terms: {} as Record<string, any>,
  } as QuerySchema;

  delete params.rows;
  delete params.page;
  delete params.foreign_keys;
  // delete params.key;
  query.terms = {
    ...params,
  };

  const response = await sendReqToController("mfu.list", query, isSocketCall);

  try {
    const result = extractResult(response);
    let list: Array<any> = [];
    if (result && typeof result === "object" && !Array.isArray(result)) {
      const resultObj = result as Record<string, number>;
      list = Object.entries(resultObj)
        .sort((a, b) => b[1] - a[1]) // sort descending by count
        .map(([id, _count]) => ({
          id,
          name: id,
        }));
    } else if (Array.isArray(result)) {
      list.push(...result);
    } else if (response.error !== "") {
      response.error = "invalid user list";
    }
    return {
      count: result?.count || list.length,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error(
      "Error parsing mfu.list (no index) response:",
      error,
      response
    );
    return {
      count: 0,
      data: [],
      error: "failed to validate user list response",
      status: response.code,
    };
  }
};

export const MostFrequentUserApiAll = async (params: any): Promise<any> => {
  const response = await sendReqToController("mfu.batch", params, isSocketCall);

  try {
    const result = extractResult(response);
    const simplified = {
      result: {} as any,
    };

    if (result?.results) {
      for (const key in result.results) {
        if (key !== "denmark") {
          simplified.result[key] = Object.keys(result.results[key]);
        } else {
          simplified.result[key] = result.results[key];
        }
      }
    }

    return {
      data: simplified,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing mfu.batch response:", error, response);
    return {
      data: [],
      error: "failed to validate user list response",
      status: response.code,
    };
  }
};

export const UpdateUserApi = async (params: any): Promise<RespUser> => {
  const response = await sendReqToController(
    "user.update",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.update response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Validate User Model",
      status: response.code,
    };
  }
};

export const AddUserApi = async (params: any): Promise<RespUser> => {
  const response = await sendReqToController(
    "user.create",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.create response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Add User Model",
      status: response.code,
    };
  }
};

export const GetUserSettingsApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "user_settings.batch_favorites",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as any,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error(
      "Error parsing user_settings.batch_favorites response:",
      error,
      response
    );
    return {
      data: {} as any,
      error: "Failed to Get User Settings",
      status: response.code,
    };
  }
};
export const ApproveUserApi = async (params: any): Promise<RespUser> => {
  const response = await sendReqToController(
    "user.approve",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.approve response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Add User Model",
      status: response.code,
    };
  }
};

export const ServiceRequestApi = async (params: any): Promise<RespUser> => {
  const response = await sendReqToController(
    "service.request",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing service.request response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Add User Model",
      status: response.code,
    };
  }
};

export const RejectUserApi = async (params: any): Promise<RespUser> => {
  const response = await sendReqToController(
    "user.reject",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const user: UserModel = result;
    if (user && Object.keys(user).length > 0) {
      return {
        data: user,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UserModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.reject response:", error, response);
    return {
      data: {} as UserModel,
      error: "Failed to Add User Model",
      status: response.code,
    };
  }
};

export const DeleteUserApi = async (
  params: UsersListParamsBody
): Promise<RespUserDelete> => {
  const response = await sendReqToController(
    "user.delete",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    // Handle both string and array responses
    const message =
      typeof result === "string"
        ? result
        : Array.isArray(result)
          ? result.join(", ")
          : String(result);
    if (message && message.length !== 0 && message !== "undefined") {
      return {
        message: message,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        message: "Error While Deleting User",
        error: response.error,
        status: response.code,
      };
    }
  } catch (error) {
    console.error("Error parsing user.delete response:", error, response);
    return {
      message: "Error While Deleting User",
      error: response.error || "Failed to parse delete response",
      status: response.code,
    };
  }
};

interface RespSocialChannel extends BaseResponse {
  data: Array<SocialChannel>;
}

export const GetSocialChannelAPI = async (
  size: number
): Promise<RespSocialChannel> => {
  const response = await sendReqToController(
    "socialconnect.list",
    {
      size: size,
      sort: { status: "desc" },
    },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const socialChannels: Array<SocialChannel> = result?.list || [];
    return {
      data: socialChannels,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error(
      "Error parsing socialconnect.list response:",
      error,
      response
    );
    return {
      data: [],
      error: "An unexpected error While Validating Social Channel",
      status: 500,
    };
  }
};

export const GetNotificationAPI = async (
  params: any
): Promise<RespSocialChannel> => {
  const response = await sendReqToController(
    "notification.get",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const noti: Array<SocialChannel> = result?.list || [];
    return {
      data: noti,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing notification.get response:", error, response);
    return {
      data: [],
      error: "An unexpected error While Validating Social Channel",
      status: 500,
    };
  }
};

export const ReadNotificationAPI = async (
  params: any
): Promise<RespSocialChannel> => {
  const response = await sendReqToController(
    "notification.read",
    params,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const noti: Array<SocialChannel> = result?.list || [];
    return {
      data: noti,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing notification.read response:", error, response);
    return {
      data: [],
      error: "An unexpected error While Validating Social Channel",
      status: 500,
    };
  }
};

export const ApproveServiceRequestAPI = async (
  id: number,
  service: string
): Promise<any> => {
  const response = await sendReqToController(
    "service.approve",
    { id, service },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const noti: any = result?.list || [];
    return {
      data: noti,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing service.approve response:", error, response);
    return {
      data: [],
      error: "An unexpected error While Approving Service Request",
      status: 500,
    };
  }
};

export const DisapproveServiceRequestAPI = async (id: number): Promise<any> => {
  const response = await sendReqToController(
    "service.reject",
    { id },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const noti: any = result?.list || [];
    return {
      data: noti,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing service.reject response:", error, response);
    return {
      data: [],
      error: "An unexpected error While Disapproving Service Request",
      status: 500,
    };
  }
};

export const GetNotificationsListAPI = async (
  params: any
): Promise<RespSocialChannel> => {
  const query: QuerySchema = {
    size: params.rows || 30,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 30),
    foreign_keys: params.foreign_keys,
    sort: { ct: "desc" },
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;
  // query.should = {
  //   ...params,
  // };
  const response = await sendReqToController(
    "notification.list",
    query,
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const noti: Array<SocialChannel> = result?.list || [];
    return {
      data: noti,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error("Error parsing notification.list response:", error, response);
    return {
      data: [],
      error: "An unexpected error While Validating Social Channel",
      status: 500,
    };
  }
};

interface RespYoutubePlaylist extends BaseResponse {
  data: Array<YoutubePlaylist>;
}

export const GetYoutubePlaylistAPI = async (
  channel_id: string
): Promise<RespYoutubePlaylist> => {
  const response = await sendReqToController(
    "youtubeplaylist.get",
    { id: channel_id },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const youtubePlaylist: Array<YoutubePlaylist> = result?.playlists || [];
    return {
      data: youtubePlaylist,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error(
      "Error parsing youtubeplaylist.get response:",
      error,
      response
    );
    return {
      data: [],
      error: "An unexpected error While Validating Youtube Playlist",
      status: 500,
    };
  }
};

export const UpdatePlaylistApi = async (
  channel_id: string
): Promise<RespYoutubePlaylist> => {
  const response = await sendReqToController(
    "youtubeplaylist.refresh",
    { channel_id },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const youtubePlaylist: Array<YoutubePlaylist> = result?.playlists || [];
    return {
      data: youtubePlaylist,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error(
      "Error parsing youtubeplaylist.refresh response:",
      error,
      response
    );
    return {
      data: [],
      error: "An unexpected error While Validating Youtube Playlist",
      status: 500,
    };
  }
};

interface RespMonetizationOptions extends BaseResponse {
  data: Array<MonetizationOptions>;
}

export const GetMonetizationOptionsAPI = async (
  channel_id: string
): Promise<RespMonetizationOptions> => {
  const response = await sendReqToController(
    "ytmonetisation.get",
    { id: channel_id },
    isSocketCall
  );
  try {
    const result = extractResult(response);
    const monetizationOptions: Array<MonetizationOptions> = Array.isArray(
      result
    )
      ? result
      : [];
    return {
      data: monetizationOptions,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    console.error(
      "Error parsing ytmonetisation.get response:",
      error,
      response
    );
    return {
      data: [],
      error: "An unexpected error While Validating Monetization",
      status: 500,
    };
  }
};

export const LogoutApi = async (params: any): Promise<any> => {
  const response = await sendReqToController("auth.logout", params, false);
  return response;
};

export const syncDenmarkAccessApi = async (
  userId: string
): Promise<{ ok: boolean }> => {
  const response = await sendReqToController(
    "user.sync_denmark_access",
    { id: userId },
    false
  );
  return { ok: response.code === 200 };
};

export const GetUserPreferenceApi = async (): Promise<any> => {
  const response = await sendReqToController(
    "user_preference.get",
    {},
    isSocketCall
  );
  try {
    return {
      data: extractResult(response),
      error: response.error,
      status: response.code,
    };
  } catch {
    return { data: null, error: "Failed to get preference", status: 500 };
  }
};

const upsertUserPreference = async (
  method: "user_preference.create" | "user_preference.update",
  order: string[]
): Promise<any> => {
  const response = await sendReqToController(
    method,
    { create_bucket: { order } },
    isSocketCall
  );
  try {
    return {
      data: extractResult(response),
      error: response.error,
      status: response.code,
    };
  } catch {
    return { data: null, error: "Failed to save preference", status: 500 };
  }
};

export const CreateUserPreferenceApi = (order: string[]) =>
  upsertUserPreference("user_preference.create", order);

export const UpdateUserPreferenceApi = (order: string[]) =>
  upsertUserPreference("user_preference.update", order);

const upsertPlaylistFavorites = async (
  method: "user_preference.create" | "user_preference.update",
  order: string[]
): Promise<any> => {
  const response = await sendReqToController(
    method,
    { playlist_favorites: { order } },
    isSocketCall
  );
  try {
    return {
      data: extractResult(response),
      error: response.error,
      status: response.code,
    };
  } catch {
    return { data: null, error: "Failed to save favorites", status: 500 };
  }
};

export const GetPlaylistFavoritesApi = async (): Promise<{
  data: string[];
  error: any;
  status: number;
}> => {
  const res = await GetUserPreferenceApi();
  const order = res?.data?.playlist_favorites?.order;
  return {
    data: Array.isArray(order) ? order : [],
    error: res?.error,
    status: res?.status,
  };
};

export const CreatePlaylistFavoritesApi = (order: string[]) =>
  upsertPlaylistFavorites("user_preference.create", order);

export const UpdatePlaylistFavoritesApi = (order: string[]) =>
  upsertPlaylistFavorites("user_preference.update", order);
